import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Collapse} from '@mui/material';
import Hls from 'hls.js';
import mpegts from 'mpegts.js';
import PropTypes from 'prop-types';
import PlayerChrome from 'components/channels/PlayerChrome';
import UltraPlayerStatsPanel from 'components/channels/UltraPlayerStatsPanel';
import useAudioTracks from 'hooks/useAudioTracks';
import {appendVid} from 'utils/antiCutEngine';
import {createHlsAudioLoader, createMpegAudioLoader} from 'utils/audioTrackLoaders';
import {playerHealth} from 'utils/playerHealth';
import {classifyPlaybackFailure, PLAYER_STATES} from 'utils/playerPlayback';
import {
  HEALTHY_RESET_MS,
  RECOVERY_BUFFER_SECONDS,
  RELAY_RECOVERY_STATES,
  RESTART_COALESCE_MS,
  TARGET_BUFFER_SECONDS,
  bufferedSecondsAhead,
  computeUltraBackoffDelay,
  evaluatePlayheadProgress,
  hlsErrorAction,
  nextUltraAttempt,
  resumePositionForSequence,
  stallBudgetMs,
  startupBufferDecision
} from 'utils/ultraPlayerEngine';
import {
  appendUltraHistory,
  formatSeconds,
  relaySampleIsFresh,
  relayStatusBackoffMs,
  reservePresentation,
  reserveTrendOverFiveSeconds,
  resolveUltraSession,
  strictBufferedSecondsAhead,
  validateRelayStatusPayload
} from 'utils/ultraPlayerStats';

const INITIAL_CONNECT_DELAY_MS = 800;
const MONITOR_INTERVAL_MS = 500;
const RECOVERED_FLASH_MS = 2500;
const STATUS_POLL_INTERVAL_MS = 1000;
const STATS_SAMPLE_INTERVAL_MS = 1000;
const SESSION_BOOTSTRAP_TIMEOUT_MS = 10000;
const STATUS_FETCH_TIMEOUT_MS = 5000;

function browserIsOnline() {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function finiteMetric(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function droppedVideoFrames(video) {
  try {
    const quality = video?.getVideoPlaybackQuality?.();
    const value = quality?.droppedVideoFrames ?? video?.webkitDroppedFrameCount;
    return finiteMetric(value);
  } catch (_) {
    return null;
  }
}

function readHlsDiagnostics(hls, video, bandwidthMeasured) {
  if (!hls) {
    return {
      edgeDistanceSeconds: null,
      hlsTargetSeconds: null,
      declaredBitrate: null,
      bandwidthEstimate: null,
      levelLabel: null
    };
  }
  try {
    const levelIndex = [hls.currentLevel, hls.loadLevel, hls.nextLoadLevel].find(
      (value) => Number.isInteger(value) && value >= 0
    );
    const level = Number.isInteger(levelIndex) ? hls.levels?.[levelIndex] : null;
    const hasLiveDetails = Boolean(hls.latestLevelDetails);
    return {
      edgeDistanceSeconds: hasLiveDetails ? finiteMetric(hls.latency) : null,
      hlsTargetSeconds: hasLiveDetails ? finiteMetric(hls.targetLatency) : null,
      declaredBitrate: finiteMetric(level?.bitrate),
      bandwidthEstimate: bandwidthMeasured ? finiteMetric(hls.bandwidthEstimate) : null,
      levelLabel: level?.name || (Number.isInteger(levelIndex) ? `Nivel ${levelIndex + 1}` : null),
      width: finiteMetric(video?.videoWidth || level?.width),
      height: finiteMetric(video?.videoHeight || level?.height)
    };
  } catch (_) {
    return {
      edgeDistanceSeconds: null,
      hlsTargetSeconds: null,
      declaredBitrate: null,
      bandwidthEstimate: null,
      levelLabel: null,
      width: finiteMetric(video?.videoWidth),
      height: finiteMetric(video?.videoHeight)
    };
  }
}

function UltraPlayer({
  playURL,
  playingName,
  viewId,
  bindPlayer,
  bindVideoElement,
  userPaused,
  onUserPausedChange,
  onRefreshVariant,
  advancedStatsEnabled
}) {
  const [playback, setPlayback] = useState({
    status: PLAYER_STATES.PREPARING,
    attempt: 0,
    retryAt: null,
    reason: null
  });
  const [bufferSeconds, setBufferSeconds] = useState(null);
  const [online, setOnline] = useState(browserIsOnline);
  const [relayStatusUrl, setRelayStatusUrl] = useState(null);
  const [relaySample, setRelaySample] = useState(null);
  const [history, setHistory] = useState([]);
  const [statsNow, setStatsNow] = useState(() => Date.now());
  const [diagnostics, setDiagnostics] = useState({
    localBufferSeconds: null,
    reserveTrend: null,
    relayState: null,
    playerState: PLAYER_STATES.PREPARING,
    edgeDistanceSeconds: null,
    hlsTargetSeconds: null,
    transport: null,
    width: null,
    height: null,
    declaredBitrate: null,
    bandwidthEstimate: null,
    levelLabel: null,
    droppedFrames: null,
    online: browserIsOnline(),
    retryAttempt: 0
  });

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const {audio, audioTracks, selectedAudioTrack} = useAudioTracks(playURL, videoRef);
  const hlsRef = useRef(null);
  const mpegtsRef = useRef(null);
  const playbackRef = useRef(playback);
  const initialTimerRef = useRef(null);
  const restartTimerRef = useRef(null);
  const recoveredTimerRef = useRef(null);
  const monitorTimerRef = useRef(null);
  const statsTimerRef = useRef(null);
  const sessionAbortRef = useRef(null);
  const sessionTimeoutRef = useRef(null);
  const sessionRequestGenerationRef = useRef(0);
  const attemptRef = useRef(0);
  const healthySinceRef = useRef(null);
  const createdAtRef = useRef(0);
  const lastProgressAtRef = useRef(0);
  const lastCurrentTimeRef = useRef(-1);
  const readyToPlayRef = useRef(false);
  const playRequestedRef = useRef(false);
  const userPausedRef = useRef(userPaused);
  const autoplayBlockedRef = useRef(false);
  const deferredFailureRef = useRef(null);
  const needsRecoveryConfirmationRef = useRef(false);
  const onlineRef = useRef(browserIsOnline());
  const compatibleModeRef = useRef(false);
  const compatibilityConfirmedRef = useRef(false);
  const bandwidthMeasuredRef = useRef(false);
  const manifestParsedRef = useRef(false);
  const mediaRecoveryAttemptsRef = useRef(0);
  const inplaceReloadAttemptsRef = useRef(0);
  const lastMediaRecoveryAtRef = useRef(0);
  const lastCcRef = useRef(-1);
  const lastFragRef = useRef(null);
  const resumeRef = useRef(null);
  const activePlaylistUrlRef = useRef(null);
  const relayRecoveringRef = useRef(false);
  const engineGenerationRef = useRef(0);
  const sessionGenerationRef = useRef(0);
  const transportRef = useRef(null);
  const relaySampleRef = useRef(null);
  const relayStatusUrlRef = useRef(null);
  const historyRef = useRef([]);
  const createEngineRef = useRef(() => {});
  const scheduleRestartRef = useRef(() => {});
  const restartNowRef = useRef(() => {});
  const beginPlaybackRef = useRef(() => {});

  useEffect(() => {
    playbackRef.current = playback;
  }, [playback]);

  useEffect(() => {
    relaySampleRef.current = relaySample;
  }, [relaySample]);

  useEffect(() => {
    relayStatusUrlRef.current = relayStatusUrl;
  }, [relayStatusUrl]);

  const reserveView = useMemo(
    () => reservePresentation(relaySample, history, statsNow),
    [history, relaySample, statsNow]
  );
  const reserveChipLabel = `Colchón Ultra ${formatSeconds(reserveView.reserveSeconds)}`;

  useEffect(() => {
    userPausedRef.current = userPaused;
    if (
      userPaused &&
      playbackRef.current.status !== PLAYER_STATES.OFFLINE &&
      playbackRef.current.status !== PLAYER_STATES.TERMINAL_ERROR
    ) {
      const next = {...playbackRef.current, status: PLAYER_STATES.PAUSED, reason: null};
      playbackRef.current = next;
      setPlayback(next);
    }
  }, [userPaused]);

  const updatePlayback = useCallback((patch) => {
    const previous = playbackRef.current;
    const next = {...previous, ...patch};
    if (patch.status && patch.status !== PLAYER_STATES.RECOVERED && recoveredTimerRef.current) {
      window.clearTimeout(recoveredTimerRef.current);
      recoveredTimerRef.current = null;
    }
    playbackRef.current = next;
    setPlayback(next);
    if (next.status !== previous.status) {
      playerHealth.report('player.stateChanged', {
        from: previous.status,
        to: next.status,
        variant: 'C',
        reason: next.reason || null
      });
    }
  }, []);

  const clearRestartTimer = useCallback(() => {
    if (restartTimerRef.current) window.clearTimeout(restartTimerRef.current);
    restartTimerRef.current = null;
  }, []);

  const destroyEngine = useCallback(() => {
    engineGenerationRef.current += 1;
    const hls = hlsRef.current;
    const mpeg = mpegtsRef.current;
    hlsRef.current = null;
    mpegtsRef.current = null;
    transportRef.current = null;
    bandwidthMeasuredRef.current = false;
    if (hls) {
      try {
        hls.destroy();
      } catch (_) {
        // The next generation owns the media element now.
      }
    }
    if (mpeg) {
      try {
        mpeg.destroy();
      } catch (_) {
        // The next generation owns the media element now.
      }
    }
    const video = videoRef.current;
    if (video) {
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch (_) {
        // Native HLS may already have detached the element.
      }
    }
  }, []);

  const deferFailureWhilePaused = useCallback(
    (failure) => {
      clearRestartTimer();
      deferredFailureRef.current = {
        retryable: failure?.retryable !== false,
        reason: failure?.reason || 'network',
        statusCode: failure?.statusCode ?? null
      };
      destroyEngine();
      updatePlayback({status: PLAYER_STATES.PAUSED, retryAt: null, reason: null});
    },
    [clearRestartTimer, destroyEngine, updatePlayback]
  );

  const showRecovered = useCallback(() => {
    if (recoveredTimerRef.current) window.clearTimeout(recoveredTimerRef.current);
    const sessionGeneration = sessionGenerationRef.current;
    needsRecoveryConfirmationRef.current = false;
    updatePlayback({status: PLAYER_STATES.RECOVERED, retryAt: null, reason: null});
    playerHealth.report('player.recovered', {variant: 'C', attempt: attemptRef.current});
    playerHealth.report('ultra.recovered', {
      attempt: attemptRef.current,
      bufferSeconds: Number(
        bufferedSecondsAhead(videoRef.current?.buffered, videoRef.current?.currentTime).toFixed(2)
      )
    });
    Promise.resolve(onRefreshVariant?.('recovered')).catch(() => {});
    recoveredTimerRef.current = window.setTimeout(() => {
      recoveredTimerRef.current = null;
      if (
        sessionGeneration !== sessionGenerationRef.current ||
        playbackRef.current.status !== PLAYER_STATES.RECOVERED
      )
        return;
      if (!onlineRef.current) updatePlayback({status: PLAYER_STATES.OFFLINE, reason: 'offline'});
      else if (userPausedRef.current || autoplayBlockedRef.current) {
        updatePlayback({
          status: PLAYER_STATES.PAUSED,
          reason: autoplayBlockedRef.current ? 'autoplayBlocked' : null
        });
      } else updatePlayback({status: PLAYER_STATES.PLAYING, reason: null});
    }, RECOVERED_FLASH_MS);
  }, [onRefreshVariant, updatePlayback]);

  const handleAutoplayBlocked = useCallback(() => {
    if (userPausedRef.current || playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR)
      return;
    playRequestedRef.current = false;
    autoplayBlockedRef.current = true;
    updatePlayback({status: PLAYER_STATES.PAUSED, retryAt: null, reason: 'autoplayBlocked'});
    playerHealth.report('player.autoplayBlocked', {variant: 'C'});
    playerHealth.report('ultra.autoplayBlocked', {});
  }, [updatePlayback]);

  const beginPlayback = useCallback(
    (mode = 'resume', available = 0) => {
      const video = videoRef.current;
      if (!video || !readyToPlayRef.current) return;
      if (userPausedRef.current) {
        updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
        return;
      }
      autoplayBlockedRef.current = false;
      playRequestedRef.current = true;
      const generation = engineGenerationRef.current;
      const sessionGeneration = sessionGenerationRef.current;
      const requestedVideo = video;
      playerHealth.report('ultra.bufferReady', {
        mode,
        bufferSeconds: Number(available.toFixed(2)),
        targetSeconds: TARGET_BUFFER_SECONDS
      });
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          if (
            generation !== engineGenerationRef.current ||
            sessionGeneration !== sessionGenerationRef.current ||
            requestedVideo !== videoRef.current
          )
            return;
          handleAutoplayBlocked();
        });
      }
    },
    [handleAutoplayBlocked, updatePlayback]
  );
  beginPlaybackRef.current = beginPlayback;

  const makeTerminal = useCallback(
    (classification) => {
      clearRestartTimer();
      deferredFailureRef.current = null;
      updatePlayback({
        status: PLAYER_STATES.TERMINAL_ERROR,
        retryAt: null,
        reason: classification.reason
      });
      // Keep terminal sticky before detaching; destroy may flush late media
      // callbacks which must never arm automatic recovery again.
      destroyEngine();
      playerHealth.report('player.terminalError', {
        variant: 'C',
        reason: classification.reason,
        statusCode: classification.statusCode
      });
    },
    [clearRestartTimer, destroyEngine, updatePlayback]
  );

  const startCompatibleTransport = useCallback(
    (urlWithVid, generation) => {
      const video = videoRef.current;
      if (!video || engineGenerationRef.current !== generation) return;
      try {
        let player;
        const isCurrent = () =>
          engineGenerationRef.current === generation && mpegtsRef.current === player;
        player = mpegts.createPlayer(
          {type: 'mse', isLive: true, url: urlWithVid},
          {
            enableWorker: false,
            customLoader: createMpegAudioLoader(mpegts.BaseLoader, audio, isCurrent),
            enableStashBuffer: true,
            stashInitialSize: 1024 * 1024,
            liveBufferLatencyChasing: false,
            autoCleanupSourceBuffer: true,
            autoCleanupMinBackwardDuration: 30,
            autoCleanupMaxBackwardDuration: 60
          }
        );
        mpegtsRef.current = player;
        transportRef.current = 'MPEG-TS compatible';
        player.on(mpegts.Events.MEDIA_INFO, () => {
          if (!isCurrent()) return;
          compatibilityConfirmedRef.current = true;
          playerHealth.report('ultra.compatibilityReady', {attempt: attemptRef.current});
        });
        player.on(mpegts.Events.ERROR, (...args) => {
          if (!isCurrent()) return;
          if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
          const classification = classifyPlaybackFailure(args, 'network');
          playerHealth.report('ultra.compatibilityError', {
            statusCode: classification.statusCode,
            reason: classification.reason
          });
          if (!compatibilityConfirmedRef.current) {
            compatibleModeRef.current = false;
            playerHealth.report('ultra.compatibilityRejected', {reason: 'noMpegtsMediaInfo'});
          }
          if (userPausedRef.current) {
            deferFailureWhilePaused(classification);
            return;
          }
          if (!classification.retryable) makeTerminal(classification);
          else scheduleRestartRef.current(classification.reason);
        });
        player.on(mpegts.Events.LOADING_COMPLETE, () => {
          if (!isCurrent()) return;
          if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
          if (userPausedRef.current) {
            deferFailureWhilePaused({retryable: true, reason: 'streamEnded'});
            return;
          }
          scheduleRestartRef.current('streamEnded');
        });
        player.attachMediaElement(video);
        bindPlayer?.(player);
        player.load();
        playerHealth.report('ultra.playerCreated', {
          transport: 'compatible-mpegts',
          attempt: attemptRef.current,
          generation
        });
      } catch (error) {
        const classification = classifyPlaybackFailure(error, 'network');
        if (userPausedRef.current) {
          deferFailureWhilePaused(classification);
          return;
        }
        if (!classification.retryable) makeTerminal(classification);
        else scheduleRestartRef.current(classification.reason);
      }
    },
    [audio, bindPlayer, deferFailureWhilePaused, makeTerminal]
  );

  const createTransport = useCallback(
    (url) => {
      if (!url || !onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }
      clearRestartTimer();
      destroyEngine();
      const video = videoRef.current;
      if (!video) return;
      const generation = engineGenerationRef.current;
      const urlWithVid = appendVid(url, viewId);

      bindVideoElement?.(video);
      createdAtRef.current = Date.now();
      lastProgressAtRef.current = createdAtRef.current;
      lastCurrentTimeRef.current = -1;
      readyToPlayRef.current = false;
      playRequestedRef.current = false;
      autoplayBlockedRef.current = false;
      manifestParsedRef.current = false;
      mediaRecoveryAttemptsRef.current = 0;
      inplaceReloadAttemptsRef.current = 0;
      lastMediaRecoveryAtRef.current = 0;
      lastCcRef.current = -1;
      lastFragRef.current = null;
      relayRecoveringRef.current = false;
      // A restart of the same relay session resumes where playback was; a
      // different playlist (channel change, replaced session) starts live.
      const resume = resumeRef.current;
      resumeRef.current = null;
      const resumeAt = resume && resume.playlistUrl === url ? resume : null;
      activePlaylistUrlRef.current = url;
      compatibilityConfirmedRef.current = false;
      bandwidthMeasuredRef.current = false;
      setBufferSeconds(null);
      updatePlayback({
        status: userPausedRef.current ? PLAYER_STATES.PAUSED : PLAYER_STATES.STABILIZING,
        retryAt: null,
        reason: null
      });
      playerHealth.report('ultra.stabilizing', {targetSeconds: TARGET_BUFFER_SECONDS});

      if (compatibleModeRef.current) {
        startCompatibleTransport(urlWithVid, generation);
        return;
      }

      audio.nativeMode = false;
      if (Hls.isSupported()) {
        let hls;
        const isCurrent = () =>
          engineGenerationRef.current === generation && hlsRef.current === hls;
        hls = new Hls({
          fLoader: createHlsAudioLoader(Hls.DefaultConfig.loader, audio, isCurrent),
          progressive: false,
          lowLatencyMode: false,
          // Manual startLoad: the position is chosen once the playlist is
          // known, so a restart can resume at the previous media sequence
          // instead of re-entering ten seconds behind the edge.
          autoStartLoad: false,
          maxBufferLength: 12,
          maxMaxBufferLength: 18,
          maxBufferSize: 64 * 1024 * 1024,
          backBufferLength: 30,
          liveSyncDuration: TARGET_BUFFER_SECONDS,
          // A finite MediaSource duration on a live stream lets the element
          // fire `ended` whenever the buffer runs dry at the edge.
          liveDurationInfinity: true,
          // No liveMaxLatencyDuration: hls.js must never hard-seek the viewer
          // forward on its own; the relay window already bounds latency.
          maxBufferHole: 0.5,
          nudgeMaxRetry: 5,
          nudgeOffset: 0.2,
          startFragPrefetch: true,
          manifestLoadingTimeOut: 10000,
          fragLoadingTimeOut: 10000,
          levelLoadingTimeOut: 10000
        });
        hlsRef.current = hls;
        transportRef.current = 'HLS.js';
        hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
          if (!isCurrent()) return;
          manifestParsedRef.current = true;
          playerHealth.report('ultra.manifestParsed', {
            levels: Array.isArray(data?.levels) ? data.levels.length : null,
            live: data?.levels?.[0]?.details?.live ?? null
          });
          const details = data?.levels?.[0]?.details || hls.levels?.[0]?.details || null;
          const position = resumeAt ? resumePositionForSequence(details, resumeAt) : -1;
          if (position >= 0) {
            playerHealth.report('ultra.resumedAtSequence', {
              sn: resumeAt.sn,
              position: Number(position.toFixed(2))
            });
          }
          try {
            hls.startLoad(position);
          } catch (_) {
            // hls.js already reports load failures through its ERROR event.
          }
        });
        hls.on(Hls.Events.FRAG_LOADED, () => {
          if (isCurrent()) bandwidthMeasuredRef.current = true;
        });
        hls.on(Hls.Events.FRAG_CHANGED, (_event, data) => {
          if (!isCurrent()) return;
          const frag = data?.frag;
          if (!frag) return;
          lastFragRef.current = {sn: frag.sn, start: frag.start, duration: frag.duration};
          if (Number.isFinite(frag.cc) && frag.cc !== lastCcRef.current) {
            const previous = lastCcRef.current;
            lastCcRef.current = frag.cc;
            if (previous >= 0) playerHealth.report('ultra.discontinuity', {cc: frag.cc});
          }
        });
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!isCurrent()) return;
          if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
          const {action, classification} = hlsErrorAction(data, {
            manifestParsed: manifestParsedRef.current,
            mediaRecoveryAttempts: mediaRecoveryAttemptsRef.current,
            inplaceReloadAttempts: inplaceReloadAttemptsRef.current
          });
          playerHealth.report('ultra.hlsError', {
            fatal: Boolean(data?.fatal),
            type: data?.type || null,
            details: data?.details || null,
            statusCode: classification.statusCode,
            action
          });

          if (userPausedRef.current && data?.fatal) {
            deferFailureWhilePaused(classification);
            return;
          }

          switch (action) {
            case 'compatible':
              compatibleModeRef.current = true;
              playerHealth.report('ultra.compatibilityFallback', {reason: 'formatMismatch'});
              Promise.resolve(onRefreshVariant?.('formatMismatch')).catch(() => {});
              scheduleRestartRef.current('transport');
              return;
            case 'terminal':
              makeTerminal(classification);
              return;
            case 'buffering':
              if (!userPausedRef.current) {
                updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'playbackStall'});
              }
              return;
            case 'ignore':
              return;
            case 'recoverMedia':
              mediaRecoveryAttemptsRef.current += 1;
              lastMediaRecoveryAtRef.current = Date.now();
              updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'transport'});
              try {
                // hls.js re-attaches and resumes at the same currentTime; the
                // source is not reloaded, so nothing already watched replays.
                hls.recoverMediaError();
                lastCurrentTimeRef.current = -1;
                return;
              } catch (_) {
                break;
              }
            case 'reloadInPlace':
              inplaceReloadAttemptsRef.current += 1;
              lastMediaRecoveryAtRef.current = Date.now();
              playerHealth.report('ultra.reloadInPlace', {
                details: data?.details || null,
                attempt: inplaceReloadAttemptsRef.current
              });
              updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'network'});
              try {
                hls.startLoad(-1);
                lastCurrentTimeRef.current = -1;
                return;
              } catch (_) {
                break;
              }
            default:
              break;
          }
          scheduleRestartRef.current(classification.reason);
        });
        // loadSource before attachMedia: attaching (and re-attaching inside
        // recoverMediaError) must never reload the manifest.
        hls.loadSource(urlWithVid);
        hls.attachMedia(video);
        playerHealth.report('ultra.playerCreated', {
          transport: 'hls.js',
          attempt: attemptRef.current,
          generation
        });
        return;
      }

      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        audio.nativeMode = true;
        transportRef.current = 'HLS nativo';
        video.src = urlWithVid;
        video.load();
        playerHealth.report('ultra.nativeFallback', {transport: 'native-hls'});
        playerHealth.report('ultra.playerCreated', {
          transport: 'native-hls',
          attempt: attemptRef.current,
          generation
        });
        return;
      }

      playerHealth.report('ultra.hlsUnsupported', {});
      makeTerminal(classifyPlaybackFailure(null, 'hlsUnsupported'));
    },
    [
      audio,
      bindVideoElement,
      clearRestartTimer,
      destroyEngine,
      deferFailureWhilePaused,
      makeTerminal,
      onRefreshVariant,
      startCompatibleTransport,
      updatePlayback,
      viewId
    ]
  );

  const createEngine = useCallback(
    (url) => {
      if (!url || !onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }
      sessionRequestGenerationRef.current += 1;
      const requestGeneration = sessionRequestGenerationRef.current;
      const sessionGeneration = sessionGenerationRef.current;
      sessionAbortRef.current?.abort();
      if (sessionTimeoutRef.current) window.clearTimeout(sessionTimeoutRef.current);
      const controller = new AbortController();
      sessionAbortRef.current = controller;
      let timedOut = false;
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, SESSION_BOOTSTRAP_TIMEOUT_MS);
      sessionTimeoutRef.current = timeoutId;
      updatePlayback({
        status: userPausedRef.current ? PLAYER_STATES.PAUSED : PLAYER_STATES.CONNECTING,
        retryAt: null,
        reason: null
      });

      resolveUltraSession(url, viewId, {signal: controller.signal})
        .then((session) => {
          if (
            controller.signal.aborted ||
            requestGeneration !== sessionRequestGenerationRef.current ||
            sessionGeneration !== sessionGenerationRef.current
          ) {
            return;
          }
          if (sessionAbortRef.current === controller) sessionAbortRef.current = null;
          window.clearTimeout(timeoutId);
          if (sessionTimeoutRef.current === timeoutId) sessionTimeoutRef.current = null;
          if (session.statusUrl) {
            if (relayStatusUrlRef.current !== session.statusUrl) {
              const initialSample = {
                state: session.state,
                upstreamReceiving: false,
                reserveSeconds: null,
                targetReserveSeconds: null,
                sampledAtMs: null,
                receivedAtMs: Date.now()
              };
              relaySampleRef.current = initialSample;
              setRelaySample(initialSample);
            }
            relayStatusUrlRef.current = session.statusUrl;
            setRelayStatusUrl(session.statusUrl);
          } else {
            relayStatusUrlRef.current = null;
            relaySampleRef.current = null;
            setRelayStatusUrl(null);
            setRelaySample(null);
          }
          createTransport(session.playlistUrl);
        })
        .catch((error) => {
          window.clearTimeout(timeoutId);
          if (sessionTimeoutRef.current === timeoutId) sessionTimeoutRef.current = null;
          if (sessionAbortRef.current === controller) sessionAbortRef.current = null;
          const isCurrentRequest =
            requestGeneration === sessionRequestGenerationRef.current &&
            sessionGeneration === sessionGenerationRef.current;
          if (timedOut && isCurrentRequest) {
            relayStatusUrlRef.current = null;
            relaySampleRef.current = null;
            setRelayStatusUrl(null);
            setRelaySample(null);
            createTransport(url);
            return;
          }
          if (!isCurrentRequest || controller.signal.aborted) return;
          const classification = classifyPlaybackFailure(error, 'network');
          if (userPausedRef.current) deferFailureWhilePaused(classification);
          else if (classification.retryable) scheduleRestartRef.current(classification.reason);
          else makeTerminal(classification);
        });
    },
    [createTransport, deferFailureWhilePaused, makeTerminal, updatePlayback, viewId]
  );
  createEngineRef.current = createEngine;

  const scheduleRestart = useCallback(
    (reason) => {
      if (restartTimerRef.current || playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR)
        return;
      if (userPausedRef.current) {
        deferFailureWhilePaused({retryable: true, reason});
        return;
      }
      const healthyMs = healthySinceRef.current ? Date.now() - healthySinceRef.current : 0;
      const next = nextUltraAttempt(attemptRef.current, healthyMs);
      const delayMs = computeUltraBackoffDelay(next);
      attemptRef.current = next;
      needsRecoveryConfirmationRef.current = true;
      healthySinceRef.current = null;
      readyToPlayRef.current = false;
      playRequestedRef.current = false;
      const lastFrag = lastFragRef.current;
      const video = videoRef.current;
      if (lastFrag && activePlaylistUrlRef.current && video && Number.isFinite(video.currentTime)) {
        resumeRef.current = {
          playlistUrl: activePlaylistUrlRef.current,
          sn: lastFrag.sn,
          offset: video.currentTime - lastFrag.start
        };
      } else {
        resumeRef.current = null;
      }
      destroyEngine();
      updatePlayback({attempt: next});
      playerHealth.report('player.retryScheduled', {variant: 'C', reason, attempt: next, delayMs});
      playerHealth.report('ultra.restartScheduled', {reason, attempt: next, delayMs, healthyMs});
      if (!onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }
      const retryAt = Date.now() + delayMs;
      const sessionGeneration = sessionGenerationRef.current;
      updatePlayback({status: PLAYER_STATES.WAITING_RETRY, retryAt, reason});
      restartTimerRef.current = window.setTimeout(() => {
        restartTimerRef.current = null;
        if (sessionGeneration !== sessionGenerationRef.current) return;
        if (userPausedRef.current) {
          deferredFailureRef.current = {retryable: true, reason, statusCode: null};
          updatePlayback({status: PLAYER_STATES.PAUSED, retryAt: null, reason: null});
          return;
        }
        playerHealth.report('ultra.restarted', {reason, attempt: attemptRef.current});
        createEngineRef.current(playURL);
      }, delayMs);
    },
    [deferFailureWhilePaused, destroyEngine, playURL, updatePlayback]
  );
  scheduleRestartRef.current = scheduleRestart;

  const restartNow = useCallback(
    (reason = 'manual') => {
      const audioChange = reason === 'audioTrack' || reason === 'resume:audioTrack';
      if (reason !== 'manual' && !audioChange) {
        // Only one bootstrap/transport at a time. Online/offline flapping or
        // a resume that races a fresh transport must not chain full reloads.
        if (sessionAbortRef.current) {
          playerHealth.report('ultra.restartCoalesced', {reason, stage: 'bootstrap'});
          return;
        }
        if (
          (hlsRef.current || mpegtsRef.current) &&
          Date.now() - createdAtRef.current < RESTART_COALESCE_MS
        ) {
          playerHealth.report('ultra.restartCoalesced', {reason, stage: 'transport'});
          return;
        }
      }
      clearRestartTimer();
      if (initialTimerRef.current) window.clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
      if (audioChange && lastFragRef.current && activePlaylistUrlRef.current) {
        resumeRef.current = {
          playlistUrl: activePlaylistUrlRef.current,
          sn: lastFragRef.current.sn,
          offset: (videoRef.current?.currentTime || 0) - lastFragRef.current.start
        };
      }
      if (audioChange) destroyEngine();
      playerHealth.report('player.retryNow', {variant: 'C', reason, attempt: attemptRef.current});
      if (!onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
        return;
      }
      createEngineRef.current(playURL);
    },
    [clearRestartTimer, destroyEngine, playURL, updatePlayback]
  );
  restartNowRef.current = restartNow;

  useEffect(() => {
    if (!relayStatusUrl || typeof fetch !== 'function') return undefined;
    const playerSessionGeneration = sessionGenerationRef.current;
    let cancelled = false;
    let hidden = document.visibilityState === 'hidden';
    let failures = 0;
    let timer = null;
    let controller = null;
    let requestGeneration = 0;
    let poll = async () => {};
    const obsolete = () =>
      cancelled ||
      playerSessionGeneration !== sessionGenerationRef.current ||
      relayStatusUrlRef.current !== relayStatusUrl;

    const clearTimer = () => {
      if (timer) window.clearTimeout(timer);
      timer = null;
    };
    const schedule = (delayMs) => {
      clearTimer();
      if (!obsolete() && !hidden) timer = window.setTimeout(poll, delayMs);
    };
    poll = async () => {
      if (obsolete() || hidden) return;
      const generation = requestGeneration + 1;
      requestGeneration = generation;
      const requestController = new AbortController();
      controller = requestController;
      let timedOut = false;
      const timeout = window.setTimeout(() => {
        timedOut = true;
        requestController.abort();
      }, STATUS_FETCH_TIMEOUT_MS);
      try {
        const response = await fetch(relayStatusUrl, {
          method: 'GET',
          headers: {Accept: 'application/json'},
          credentials: 'same-origin',
          cache: 'no-store',
          signal: requestController.signal
        });
        if (!response.ok) throw new Error(`status:${response.status}`);
        const sample = validateRelayStatusPayload(await response.json(), Date.now());
        if (!sample) throw new Error('invalid-status-payload');
        if (obsolete() || hidden || generation !== requestGeneration) return;
        failures = 0;
        relaySampleRef.current = sample;
        setRelaySample(sample);
        schedule(STATUS_POLL_INTERVAL_MS);
      } catch (error) {
        if (obsolete() || hidden || generation !== requestGeneration) return;
        if (error?.name === 'AbortError' && !timedOut) return;
        failures += 1;
        schedule(relayStatusBackoffMs(failures));
      } finally {
        window.clearTimeout(timeout);
        if (controller === requestController) controller = null;
      }
    };
    const onVisibilityChange = () => {
      if (obsolete()) return;
      hidden = document.visibilityState === 'hidden';
      clearTimer();
      if (hidden) {
        requestGeneration += 1;
        controller?.abort();
      } else {
        failures = 0;
        poll();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    if (!hidden) poll();
    return () => {
      cancelled = true;
      requestGeneration += 1;
      clearTimer();
      controller?.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [relayStatusUrl]);

  useEffect(() => {
    const sampleStats = () => {
      const now = Date.now();
      const visible = document.visibilityState !== 'hidden';
      const video = videoRef.current;
      const hasTransport = Boolean(transportRef.current);
      const localBufferSeconds =
        visible && hasTransport
          ? strictBufferedSecondsAhead(video?.buffered, video?.currentTime)
          : null;
      const relay = relaySampleRef.current;
      const relayFresh = visible && relaySampleIsFresh(relay, now);
      const nextHistory = appendUltraHistory(
        historyRef.current,
        {
          reserveSeconds: relayFresh ? relay?.reserveSeconds : null,
          localBufferSeconds,
          targetReserveSeconds: Number.isFinite(relay?.targetReserveSeconds)
            ? relay.targetReserveSeconds
            : null
        },
        now
      );
      historyRef.current = nextHistory;
      setHistory(nextHistory);
      setStatsNow(now);

      const hlsMetrics = readHlsDiagnostics(hlsRef.current, video, bandwidthMeasuredRef.current);
      setDiagnostics({
        ...hlsMetrics,
        width: hlsMetrics.width ?? finiteMetric(video?.videoWidth),
        height: hlsMetrics.height ?? finiteMetric(video?.videoHeight),
        localBufferSeconds,
        reserveTrend: reserveTrendOverFiveSeconds(nextHistory, now),
        relayState: relayFresh ? relay?.state || null : null,
        playerState: playbackRef.current.status,
        transport: transportRef.current,
        droppedFrames: droppedVideoFrames(video),
        online: onlineRef.current,
        retryAttempt: attemptRef.current
      });
    };

    sampleStats();
    statsTimerRef.current = window.setInterval(sampleStats, STATS_SAMPLE_INTERVAL_MS);
    return () => {
      if (statsTimerRef.current) window.clearInterval(statsTimerRef.current);
      statsTimerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onPlaying = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (userPausedRef.current) {
        video.pause();
        updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
        return;
      }
      if (!onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.OFFLINE, reason: 'offline'});
        return;
      }
      autoplayBlockedRef.current = false;
      healthySinceRef.current = Date.now();
      lastProgressAtRef.current = Date.now();
      if (
        needsRecoveryConfirmationRef.current ||
        playbackRef.current.status === PLAYER_STATES.BUFFERING ||
        playbackRef.current.status === PLAYER_STATES.OFFLINE
      ) {
        const available = bufferedSecondsAhead(video.buffered, video.currentTime);
        if (available >= RECOVERY_BUFFER_SECONDS && onlineRef.current) showRecovered();
        else updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'playbackStall'});
      } else {
        updatePlayback({status: PLAYER_STATES.PLAYING, retryAt: null, reason: null});
      }
    };
    const onWaiting = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (!userPausedRef.current && onlineRef.current) {
        updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'playbackStall'});
      }
    };
    const onEnded = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (userPausedRef.current) return;
      if (hlsRef.current) {
        // A live hls.js session has no end. Treat it as a buffer underrun and
        // let the watchdog decide instead of replacing the session.
        playerHealth.report('ultra.endedIgnored', {});
        updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'playbackStall'});
        return;
      }
      scheduleRestartRef.current('streamEnded');
    };
    const onError = () => {
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      if (!hlsRef.current && !userPausedRef.current) scheduleRestartRef.current('network');
    };
    video.addEventListener('playing', onPlaying);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onWaiting);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);
    return () => {
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onWaiting);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
    };
  }, [showRecovered, updatePlayback]);

  useEffect(() => {
    monitorTimerRef.current = window.setInterval(() => {
      const video = videoRef.current;
      if (
        !video ||
        restartTimerRef.current ||
        (!hlsRef.current && !mpegtsRef.current && !video.src)
      )
        return;
      const now = Date.now();
      const available = bufferedSecondsAhead(video.buffered, video.currentTime);
      setBufferSeconds((current) =>
        current === null || Math.abs(current - available) >= 0.2 ? available : current
      );

      if (document.visibilityState === 'hidden') {
        lastProgressAtRef.current = now;
        return;
      }
      if (!readyToPlayRef.current) {
        const decision = startupBufferDecision(available, now - createdAtRef.current);
        if (decision === 'ready' || decision === 'degraded') {
          readyToPlayRef.current = true;
          if (userPausedRef.current) updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
          else beginPlaybackRef.current(decision, available);
        } else if (decision === 'restart') {
          scheduleRestartRef.current('emptyStartupBuffer');
        }
        return;
      }
      if (userPausedRef.current || autoplayBlockedRef.current) {
        lastProgressAtRef.current = now;
        return;
      }
      const {progressed, reset} = evaluatePlayheadProgress(
        lastCurrentTimeRef.current,
        video.currentTime
      );
      if (progressed) {
        if (reset) {
          playerHealth.report('ultra.timelineReset', {
            from: Number(lastCurrentTimeRef.current.toFixed(2)),
            to: Number(video.currentTime.toFixed(2))
          });
        }
        lastCurrentTimeRef.current = video.currentTime;
        lastProgressAtRef.current = now;
        if (
          playbackRef.current.status === PLAYER_STATES.BUFFERING &&
          available >= RECOVERY_BUFFER_SECONDS &&
          onlineRef.current
        ) {
          showRecovered();
        }
      }
      if (
        lastMediaRecoveryAtRef.current &&
        now - lastMediaRecoveryAtRef.current >= HEALTHY_RESET_MS
      ) {
        mediaRecoveryAttemptsRef.current = 0;
        inplaceReloadAttemptsRef.current = 0;
        lastMediaRecoveryAtRef.current = 0;
      }
      if (
        playbackRef.current.status === PLAYER_STATES.PLAYING &&
        available < 0.75 &&
        video.readyState < 3
      ) {
        updatePlayback({status: PLAYER_STATES.BUFFERING, reason: 'playbackStall'});
      }
      const relay = relaySampleRef.current;
      const relayFresh = relaySampleIsFresh(relay, now);
      const relayRecovering = relayFresh && RELAY_RECOVERY_STATES.includes(relay?.state);
      if (relayRecovering !== relayRecoveringRef.current) {
        relayRecoveringRef.current = relayRecovering;
        // Fresh real media after a relay recovery gets a full stall budget.
        if (!relayRecovering) lastProgressAtRef.current = now;
        playerHealth.report('ultra.relayRecoveryObserved', {
          state: relay?.state || null,
          active: relayRecovering
        });
      }
      if (now - lastProgressAtRef.current >= stallBudgetMs(relay?.state, relayFresh)) {
        scheduleRestartRef.current('playbackStall');
      }
    }, MONITOR_INTERVAL_MS);
    return () => {
      if (monitorTimerRef.current) window.clearInterval(monitorTimerRef.current);
      monitorTimerRef.current = null;
    };
  }, [showRecovered, updatePlayback]);

  useEffect(() => {
    const handleOffline = () => {
      onlineRef.current = false;
      setOnline(false);
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      clearRestartTimer();
      if (recoveredTimerRef.current) window.clearTimeout(recoveredTimerRef.current);
      updatePlayback({status: PLAYER_STATES.OFFLINE, retryAt: null, reason: 'offline'});
      playerHealth.report('player.offline', {variant: 'C'});
    };
    const handleOnline = () => {
      onlineRef.current = true;
      setOnline(true);
      playerHealth.report('player.online', {variant: 'C'});
      if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
      const video = videoRef.current;
      const available = bufferedSecondsAhead(video?.buffered, video?.currentTime);
      if (userPausedRef.current) {
        updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
      } else if ((hlsRef.current || mpegtsRef.current) && available >= RECOVERY_BUFFER_SECONDS) {
        showRecovered();
      } else {
        restartNowRef.current('online');
      }
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [clearRestartTimer, showRecovered, updatePlayback]);

  useEffect(() => {
    sessionGenerationRef.current += 1;
    const sessionGeneration = sessionGenerationRef.current;
    sessionRequestGenerationRef.current += 1;
    sessionAbortRef.current?.abort();
    sessionAbortRef.current = null;
    if (sessionTimeoutRef.current) window.clearTimeout(sessionTimeoutRef.current);
    sessionTimeoutRef.current = null;
    attemptRef.current = 0;
    healthySinceRef.current = null;
    compatibleModeRef.current = false;
    deferredFailureRef.current = null;
    needsRecoveryConfirmationRef.current = false;
    compatibilityConfirmedRef.current = false;
    setBufferSeconds(null);
    relayStatusUrlRef.current = null;
    relaySampleRef.current = null;
    historyRef.current = [];
    setRelayStatusUrl(null);
    setRelaySample(null);
    setHistory([]);
    setStatsNow(Date.now());
    clearRestartTimer();
    destroyEngine();
    updatePlayback({status: PLAYER_STATES.PREPARING, attempt: 0, retryAt: null, reason: null});
    initialTimerRef.current = window.setTimeout(() => {
      initialTimerRef.current = null;
      if (sessionGeneration !== sessionGenerationRef.current) return;
      if (onlineRef.current) createEngineRef.current(playURL);
      else updatePlayback({status: PLAYER_STATES.OFFLINE, reason: 'offline'});
    }, INITIAL_CONNECT_DELAY_MS);
    return () => {
      sessionGenerationRef.current += 1;
      sessionRequestGenerationRef.current += 1;
      sessionAbortRef.current?.abort();
      sessionAbortRef.current = null;
      if (sessionTimeoutRef.current) window.clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
      if (initialTimerRef.current) window.clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
      clearRestartTimer();
      if (recoveredTimerRef.current) window.clearTimeout(recoveredTimerRef.current);
      recoveredTimerRef.current = null;
      destroyEngine();
    };
  }, [clearRestartTimer, destroyEngine, playURL, updatePlayback]);

  const requestPause = useCallback(() => {
    if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
    userPausedRef.current = true;
    autoplayBlockedRef.current = false;
    if (restartTimerRef.current) {
      deferredFailureRef.current = {
        retryable: true,
        reason: playbackRef.current.reason || 'network',
        statusCode: null
      };
      clearRestartTimer();
    }
    onUserPausedChange(true);
    videoRef.current?.pause();
    updatePlayback({status: PLAYER_STATES.PAUSED, reason: null});
    playerHealth.report('player.userPause', {variant: 'C'});
    playerHealth.report('ultra.userPause', {});
  }, [clearRestartTimer, onUserPausedChange, updatePlayback]);

  const requestPlay = useCallback(() => {
    if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
    userPausedRef.current = false;
    autoplayBlockedRef.current = false;
    onUserPausedChange(false);
    playerHealth.report('player.userPlay', {variant: 'C'});
    playerHealth.report('ultra.userPlay', {});
    const deferredFailure = deferredFailureRef.current;
    deferredFailureRef.current = null;
    if (deferredFailure) {
      if (!deferredFailure.retryable) {
        makeTerminal(deferredFailure);
      } else {
        restartNowRef.current(`resume:${deferredFailure.reason}`);
      }
      return;
    }
    if (
      (!hlsRef.current && !mpegtsRef.current && !videoRef.current?.src) ||
      playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR
    ) {
      restartNowRef.current('userPlay');
      return;
    }
    if (readyToPlayRef.current) {
      const available = bufferedSecondsAhead(
        videoRef.current?.buffered,
        videoRef.current?.currentTime
      );
      beginPlaybackRef.current('user', available);
    } else {
      updatePlayback({status: PLAYER_STATES.STABILIZING, reason: null});
    }
  }, [makeTerminal, onUserPausedChange, updatePlayback]);

  const selectAudioTrack = (id) => {
    if (playbackRef.current.status === PLAYER_STATES.TERMINAL_ERROR) return;
    if (deferredFailureRef.current && !deferredFailureRef.current.retryable) return;
    if (audio.select(id)) return;
    if (userPausedRef.current) {
      deferredFailureRef.current = {retryable: true, reason: 'audioTrack', statusCode: null};
      return;
    }
    restartNowRef.current('audioTrack');
  };

  return (
    <>
      <PlayerChrome
        containerRef={containerRef}
        videoRef={videoRef}
        audioTracks={audioTracks}
        selectedAudioTrack={selectedAudioTrack}
        onAudioTrackChange={selectAudioTrack}
        status={playback.status}
        variant="C"
        playingName={playingName}
        attempt={playback.attempt}
        retryAt={playback.retryAt}
        reason={playback.reason}
        bufferSeconds={bufferSeconds}
        userPaused={userPaused}
        online={online}
        ultraReserveLabel={reserveChipLabel}
        ultraReserveStatus={reserveView.label}
        ultraReserveColor={reserveView.color}
        onRequestPlay={requestPlay}
        onRequestPause={requestPause}
        onRetryNow={() => restartNow('manual')}
      >
        <video
          ref={videoRef}
          playsInline
          preload="auto"
          style={{width: '100%', height: '100%', display: 'block', objectFit: 'contain'}}
        >
          Tu navegador no permite reproducir vídeo.
        </video>
      </PlayerChrome>
      <div id="ultra-advanced-panel">
        <Collapse in={advancedStatsEnabled} timeout="auto" unmountOnExit>
          <UltraPlayerStatsPanel
            history={history}
            now={statsNow}
            presentation={reserveView}
            diagnostics={diagnostics}
          />
        </Collapse>
      </div>
    </>
  );
}

UltraPlayer.propTypes = {
  playURL: PropTypes.string.isRequired,
  playingName: PropTypes.string,
  viewId: PropTypes.string.isRequired,
  bindPlayer: PropTypes.func,
  bindVideoElement: PropTypes.func,
  userPaused: PropTypes.bool.isRequired,
  onUserPausedChange: PropTypes.func.isRequired,
  onRefreshVariant: PropTypes.func,
  advancedStatsEnabled: PropTypes.bool
};

UltraPlayer.defaultProps = {
  advancedStatsEnabled: false
};

export default UltraPlayer;
